import React from "react";

import { Navigate, Outlet, useLocation } from "react-router-dom";

import Loader from "@/components/Loader/Loader";
import Sidebar from "@/components/SideBar/SideBar";
import { resolvePageId } from "@/constants/pageIds";
import { useAuth } from "@/context/AuthContext";
import { PagePermissionsProvider, usePagePermissions } from "@/context/PagePermissionsContext";
import { SidebarLayoutProvider, useSidebarLayout } from "@/context/SidebarLayoutContext";

const AppShellInner: React.FC = () => {
  const { isCollapsed, toggleCollapsed } = useSidebarLayout();
  const { role, loading, isAuthenticated } = useAuth();
  const { hasPageAccess } = usePagePermissions();
  const location = useLocation();

  // Block all rendering until auth resolves — prevents sidebar flash on 403
  if (loading) {
    return <Loader classname="fullscreen-loader" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Access guard — redirect before sidebar renders
  const pageId = resolvePageId(location.pathname);
  if (pageId && !hasPageAccess(pageId, role)) {
    return <Navigate to="/error?status=403" replace />;
  }

  return (
    <>
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleCollapsed} />
      <Outlet />
    </>
  );
};

const AppShell: React.FC = () => {
  return (
    <SidebarLayoutProvider>
      <PagePermissionsProvider>
        <AppShellInner />
      </PagePermissionsProvider>
    </SidebarLayoutProvider>
  );
};

export default AppShell;
